import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CurrencyContext = createContext(null);

const fallbackCurrency = {
  code: "INR",
  country: "India",
  countryCode: "IN",
  rate: 1,
};

// Helper function to fetch IP location with fallback support
const fetchLocationData = async () => {
  // Primary API: ipapi.co
  try {
    const res = await fetch("https://ipapi.co/json/");
    if (!res.ok) throw new Error(`ipapi.co failed with status ${res.status}`);
    const data = await res.json();
    
    return {
      currency: data.currency,
      country: data.country_name,
      countryCode: data.country_code,
    };
  } catch (primaryError) {
    // Try the backup location service silently.
  }

  // Backup API: ip-api.com
  try {
    const res = await fetch("http://ip-api.com/json/?fields=status,country,countryCode,currency");
    if (!res.ok) throw new Error(`ip-api.com failed with status ${res.status}`);
    const data = await res.json();
    
    if (data.status !== "success") throw new Error("ip-api.com returned unsuccessful status");

    return {
      currency: data.currency,
      country: data.country,
      countryCode: data.countryCode,
    };
  } catch (backupError) {
    return null;
  }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(fallbackCurrency);
  const [rates, setRates] = useState({ INR: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const detectVisitorCurrency = async () => {
      try {
        setLoading(true);

        // Fetch location (with fallback) and exchange rates in parallel
        const [locationData, ratesResponse] = await Promise.all([
          fetchLocationData(),
          fetch("https://open.er-api.com/v6/latest/INR").catch(() => {
            return null;
          }),
        ]);

        const ratesData = ratesResponse ? await ratesResponse.json() : null;

        if (!active) return;

        const detectedCode = locationData?.currency || "INR";
        const exchangeRates = ratesData?.rates || {};
        const detectedRate = Number(exchangeRates[detectedCode] || 1);

        const activeCurrency = {
          code: detectedCode,
          country: locationData?.country || "India",
          countryCode: locationData?.countryCode || "IN",
          rate: detectedRate,
        };

        if (ratesData?.rates) {
          setRates(exchangeRates);
        }
        
        setCurrency(activeCurrency);
      } catch (error) {
        if (active) {
          setCurrency(fallbackCurrency);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    detectVisitorCurrency();

    return () => {
      active = false;
    };
  }, []);

  const getCurrencySymbol = (currencyCode) => {
    try {
      return (
        new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currencyCode,
          currencyDisplay: "narrowSymbol",
        })
          .formatToParts(0)
          .find((part) => part.type === "currency")?.value || currencyCode
      );
    } catch {
      return currencyCode;
    }
  };

  const value = useMemo(
    () => ({
      ...currency,
      loading,
      symbol: getCurrencySymbol(currency.code),

      convert: (inrValue) => {
        const converted = Number(inrValue || 0) * (rates[currency.code] || currency.rate || 1);
        return converted;
      },

      format: (inrValue) => {
        const converted = Number(inrValue || 0) * (rates[currency.code] || currency.rate || 1);
        try {
          return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currency.code,
            maximumFractionDigits: 2,
          }).format(converted);
        } catch {
          return `${getCurrencySymbol(currency.code)}${converted.toFixed(2)}`;
        }
      },
    }),
    [currency, rates, loading]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () =>
  useContext(CurrencyContext) || {
    ...fallbackCurrency,
    loading: false,
    symbol: "₹",
    convert: (value) => Number(value || 0),
    format: (value) => `₹${Number(value || 0).toFixed(2)}`,
  };

export default CurrencyProvider;